import {
  call,
  put,
  take,
  fork,
  cancel,
  takeEvery,
  takeLatest,
  delay,
  race,
  select,
} from "redux-saga/effects";
import { eventChannel } from "redux-saga";
import { LOCATION_CHANGE } from "connected-react-router";
import { actions, constants, selector } from "./redux";
import Api from "../../utils/Api";
import { subscribeNotifications } from "../../utils/cable";
import { addItem as notify } from "../notifications/actions";
import { deleteItem as deleteDialog } from "../dialog-manager/actions";

// How often to poll as a FALLBACK while the WebSocket is down. The
// happy path pushes over WS and never polls; this only kicks in if
// the cable connection drops. Paused while the tab is hidden so
// background tabs don't hammer the server.
export const FALLBACK_POLL_MS = 120 * 1000;

export function* fetchItems() {
  try {
    const featureState = yield select(selector);

    const data = yield call(Api.notificationManager.index, featureState);

    yield call(fetchNotificationCount);
    yield put(actions.addItems(data));
    yield call(markAllShown);
  } catch (e) {
    console.error(e);

    yield put(actions.loadItemsError(e));
  }
}

export function* clearAllNotifications() {
  try {
    yield call(Api.notificationManager.clearAll);
    yield put(deleteDialog());
    yield put(actions.loadItemsRequest()); // through the list watcher (CREW-1268)
    yield call(fetchNotificationCount);

    yield put(
      notify({
        type: "success",
        title: "Success",
        message: "All notifications cleared successfully",
      })
    );
  } catch (e) {
    console.error(e);
  }
}

export function* clearNotification({ payload }) {
  try {
    yield call(Api.notificationManager.markAsRead, payload);
    yield put(actions.loadItemsRequest()); // through the list watcher (CREW-1268)
    yield call(fetchNotificationCount);
  } catch (e) {
    console.error(e);
  }
}

export function* markAllShown() {
  try {
    yield call(Api.notificationManager.markAllShown);
  } catch (e) {
    console.error(e);
  }
}

export function* showPopUp({ payload }) {
  const { primary_text, secondary_text } = payload;

  const snackbarData = {
    type: "info",
    title: primary_text,
  };

  if (secondary_text) {
    snackbarData.message = secondary_text;
  }

  try {
    yield put(notify({ ...snackbarData }));
  } catch (e) {
    console.error(e);
  }
}

// Page Visibility API: every open Crewisor tab used to poll the
// notification count/pop-up on a timer regardless of whether anyone
// was looking at it — N background tabs = N pointless requests every
// minute. Skip the periodic polls while the tab is hidden, and do a
// single fresh fetch the moment it becomes visible again so the badge
// is up to date when the user returns.
export const isTabHidden = () =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

export function* fetchNotificationCount() {
  try {
    const { data } = yield call(Api.notificationManager.notificationCount);

    yield put(actions.setNotificationCount(data));
  } catch (e) {
    console.error(e);
  }
}

// The 60s count poll and the 180s pop-up poll that used to live here are
// gone: the socket below pushes both, and `fallbackPolling` covers the
// window where it is down. What survives is the tab-visibility refetch —
// a sleeping laptop can miss pushes, and this closes the gap before the
// cable finishes reconnecting.
function createVisibilityChannel() {
  return eventChannel((emit) => {
    const handler = () => emit(document.visibilityState);
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  });
}

export function* watchTabVisible() {
  if (typeof document === "undefined") return;

  const channel = createVisibilityChannel();
  try {
    while (true) {
      const state = yield take(channel);
      if (state === "visible") yield call(fetchNotificationCount);
    }
  } finally {
    channel.close();
  }
}

// CREW-1188 — how long a burst of new notifications is gathered into one toast
// (a job often creates several at once). The same 3 s the old debounce used.
export const TOAST_WINDOW_MS = 3000;

const toastFor = (batch) => {
  if (batch.length === 1) {
    const { primary_text, secondary_text } = batch[0];
    return { primary_text, secondary_text };
  }
  return {
    primary_text: `You have ${batch.length} new notifications`,
    secondary_text: "Open Notifications to see them",
  };
};

// One toast per burst: the first notification opens a window, each one
// arriving inside it extends it, and when it closes the batch is shown once.
export function* collectToasts() {
  while (true) {
    const { payload } = yield take(constants.SHOW_POP_UP);
    const batch = [payload];

    while (true) {
      const { more } = yield race({ more: take(constants.SHOW_POP_UP), done: delay(TOAST_WINDOW_MS) });
      if (!more) break;
      batch.push(more.payload);
    }

    yield call(showPopUp, { payload: toastFor(batch) });
  }
}

// ---------------------------------------------------------------
// WebSocket (ActionCable) — primary transport.
//
// The BE pushes `{ type:'state', unread_count }` on subscribe and whenever
// a read/shown flag changes, and `{ type:'created', unread_count,
// notification }` once per new notification — the only thing that shows a
// toast (CREW-1188). The toast used to be a summary of every notification
// not yet shown, sent with every push and fetched on every page change.
// ---------------------------------------------------------------

function createNotificationsChannel() {
  return eventChannel((emit) => {
    const unsubscribe = subscribeNotifications({
      onConnected: () => emit({ kind: "connected" }),
      onDisconnected: () => emit({ kind: "disconnected" }),
      onState: (data) => emit({ kind: "state", data: data || {} }),
    });
    return unsubscribe;
  });
}

export function* handleSocketMessage(data) {
  if (typeof data.unread_count === "number") {
    yield put(actions.setNotificationCount({ unread_count: data.unread_count }));
  }
  if (data.type === "created" && data.notification) {
    yield put(actions.showPopUp(data.notification));
  }
}

// Fallback HTTP polling — only runs while the socket is down.
export function* fallbackPolling() {
  while (true) {
    yield delay(FALLBACK_POLL_MS);
    // `visibilityState === "hidden"` rather than the legacy `document.hidden`
    // boolean — same helper the visibility channel above reads.
    if (isTabHidden()) continue;
    yield call(fetchNotificationCount);
  }
}

function* watchNotificationsSocket() {
  while (true) {
    // Don't open a socket until the user is authenticated.
    while (!localStorage.getItem("access-token")) {
      yield delay(5000);
    }

    const channel = yield call(createNotificationsChannel);
    let pollTask = null;

    try {
      while (true) {
        const ev = yield take(channel);

        if (ev.kind === "state") {
          yield call(handleSocketMessage, ev.data);
        } else if (ev.kind === "connected") {
          if (pollTask) {
            yield cancel(pollTask);
            pollTask = null;
          }
        } else if (ev.kind === "disconnected") {
          // Socket dropped → start the fallback poll until it returns.
          if (!pollTask) pollTask = yield fork(fallbackPolling);
        }
      }
    } finally {
      if (pollTask) yield cancel(pollTask);
      channel.close();
    }
  }
}

/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 */
export default function getFeatureSagas() {
  return [
    // takeLatest (CREW-1268): a newer request cancels the one in flight, so an
    // older answer arriving last cannot overwrite the list.
    takeLatest(
      [constants.LOAD_ITEMS_REQUEST, constants.CHANGE_PAGE_NUMBER],
      fetchItems
    ),
    // One-shot bootstrap so the badge is correct on first paint /
    // navigation, before the socket subscribe round-trips. Real-time
    // updates after that come over the WebSocket, not a timer.
    takeEvery(LOCATION_CHANGE, fetchNotificationCount),
    // Kept from the polling era: a fresh count the moment the tab is looked
    // at again. The timer loops it used to sit beside are gone.
    fork(watchTabVisible),
    takeEvery(constants.CLEAR_ALL_NOTIFICATIONS, clearAllNotifications),
    takeEvery(constants.CLEAR_NOTIFICATION, clearNotification),
    fork(collectToasts),
    // Primary real-time transport (replaces the 60s/180s poll loops).
    fork(watchNotificationsSocket),
  ];
}
